import { Response, CartItem, UserSubscriptionUpdate } from "@version2/domain-package";
import type { MessageInstance } from "@version2/ui-package";
import { apiPost, apiPatch } from "./client";
import * as Sentry from "@sentry/react";

/**
 * Error types for better error categorization
 */
enum CheckoutErrorType {
	NETWORK = "network",
	VALIDATION = "validation",
	SERVER = "server",
	UNKNOWN = "unknown",
}

/**
 * Categorize error for better handling
 */
const categorizeError = (error: any): CheckoutErrorType => {
	if (!error) return CheckoutErrorType.UNKNOWN;
	
	// Network errors (timeout, connection issues)
	if (
		error.name === "NetworkError" ||
		error.code === "ECONNABORTED" ||
		error.code === "ETIMEDOUT" ||
		(typeof error === "object" && error.message && (
			error.message.toLowerCase().includes("network") ||
			error.message.toLowerCase().includes("timeout") ||
			error.message.toLowerCase().includes("connection")
		))
	) {
		return CheckoutErrorType.NETWORK;
	}
	
	// Validation errors (4xx status codes)
	if (error.status && error.status >= 400 && error.status < 500) {
		return CheckoutErrorType.VALIDATION;
	}
	
	// Server errors (5xx status codes)
	if (error.status && error.status >= 500) {
		return CheckoutErrorType.SERVER;
	}
	
	return CheckoutErrorType.UNKNOWN;
};

/**
 * Get user-friendly error message based on error type
 */
const getErrorMessage = (errorType: CheckoutErrorType): string => {
	switch (errorType) {
		case CheckoutErrorType.NETWORK:
			return "Network error. Please check your connection and try again.";
		case CheckoutErrorType.VALIDATION:
			return "Invalid request. Please check your information and try again.";
		case CheckoutErrorType.SERVER:
			return "Server error. Please try again in a few moments.";
		default:
			return "Error updating your subscription, please try again later.";
	}
};

/**
 * Update a subscription
 */
export const updateSubscription = async (
	message: MessageInstance,
	items: CartItem[],
	internalSubscriptionId: string,
	checkoutId: string
): Promise<Response> => {
	try {
		const response = await apiPatch("/v1/checkout/subscriptions", {
			items: items.map((item) => item.toJson()),
			internal_subscription_id: internalSubscriptionId,
			checkout_id: checkoutId,
		});
		
		if (!response.success) {
			throw response;
		}
		return response;
	} catch (error) {
		console.error("Error updating subscription:", error);
		
		const errorType = categorizeError(error);
		
		// Capture in Sentry with context
		Sentry.captureException(error, {
			level: "error",
			tags: {
				api_endpoint: "/v1/checkout/subscriptions",
				checkout_id: checkoutId,
				subscription_id: internalSubscriptionId,
				error_type: errorType,
			},
			extra: {
				items_count: items.length,
			},
		});
		
		// Show appropriate error message
		message.error(getErrorMessage(errorType));
		throw error;
	}
};

/**
 * Preview subscription update before committing
 */
export const previewUpdateSubscription = async (
	message: MessageInstance,
	items: CartItem[],
	internalSubscriptionId: string
): Promise<UserSubscriptionUpdate> => {
	try {
		const response = await apiPost("/v1/checkout/subscriptions/preview", {
			items: items.map((item) => item.toJson(false)),
			internal_subscription_id: internalSubscriptionId,
		});
		
		if (!response.success) {
			throw response;
		}
		return UserSubscriptionUpdate.fromJson(response.data);
	} catch (error) {
		console.error("Error previewing subscription update:", error);
		
		const errorType = categorizeError(error);
		
		// Capture in Sentry with context
		Sentry.captureException(error, {
			level: "error",
			tags: {
				api_endpoint: "/v1/checkout/subscriptions/preview",
				subscription_id: internalSubscriptionId,
				error_type: errorType,
			},
			extra: {
				items_count: items.length,
			},
		});
		
		// Show appropriate error message
		message.error(getErrorMessage(errorType));
		throw error;
	}
};
